import type { LineBookerSession } from '@bb/shared';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLineAppConfig, useLineSession } from '@/features/line-app/api';
import { liffIdentify } from '@/features/line-app/liff';
import { LineSessionContext } from '@/features/line-app/session-context';
import { ensureNamespace } from '@/lib/i18n';

/**
 * The LINE MINI App: signs the customer in, once, for both screens beneath it.
 *
 * A layout rather than two independent routes because they share everything
 * that matters — the LIFF app, the connection, the customer. Making booking and
 * 「予約の確認・変更」 separate top-level routes meant each ran `liff.init` and
 * minted its own session, so moving between them cost a round trip to LINE and
 * the two could disagree about friendship. It also, less subtly, meant the link
 * between them did not work at all: the router made one the parent of the other
 * and a parent without an <Outlet /> never renders its child.
 */
export const Route = createFileRoute('/line/app/$connectionId')({
  /*
    `bbsetup` is the wizard's step 6. The shop opens this link on their own
    phone, LIFF passes the query string through to the page, and sending it back
    with the ID token is how Business Box learns which LINE user is the shop.
  */
  validateSearch: (s: Record<string, unknown>): { bbsetup?: string } =>
    typeof s.bbsetup === 'string' ? { bbsetup: s.bbsetup } : {},
  loader: () => ensureNamespace('booking'),
  component: LineAppLayout,
});

type Phase =
  | { kind: 'loading' }
  | { kind: 'outside' }
  | { kind: 'failed'; error: string }
  | { kind: 'ready'; session: LineBookerSession };

function LineAppLayout() {
  const { connectionId } = Route.useParams();
  const { bbsetup } = Route.useSearch();
  const { t } = useTranslation(['booking', 'common']);
  const { data: config, isError: configFailed } = useLineAppConfig(connectionId);
  const session = useLineSession(connectionId);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    void (async () => {
      try {
        const identity = await liffIdentify(config.liffId);
        if (cancelled) return;
        // No identity is the ordinary case outside LINE — a shop testing the
        // link on a desktop — and must read as "open this in LINE", not as a
        // failure.
        if (!identity) return setPhase({ kind: 'outside' });
        const result = await session.mutateAsync({
          ...identity,
          ...(bbsetup ? { setupToken: bbsetup } : {}),
        });
        if (!cancelled) setPhase({ kind: 'ready', session: result });
      } catch (err) {
        if (!cancelled) {
          setPhase({ kind: 'failed', error: err instanceof Error ? err.message : 'unknown' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only when the config arrives: re-running would mint a second session.
  }, [config, session.mutateAsync, bbsetup]);

  /*
    The connection this URL names is gone, which in practice means one thing:
    the endpoint URL in LINE's console still points at a connection the shop
    replaced by reconnecting. Worth its own message — the old one blamed the
    shop's setup and left a customer with nothing to do about it.
  */
  if (configFailed) {
    return (
      <Notice title={t('booking:line.linkOutOfDate')} body={t('booking:line.linkOutOfDateBody')} />
    );
  }
  if (!config || phase.kind === 'loading') return <Notice title={t('common:status.loading')} />;
  if (phase.kind === 'outside') {
    return <Notice title={t('booking:line.openInLine')} body={t('booking:line.openInLineBody')} />;
  }
  if (phase.kind === 'failed') {
    return <Notice title={t('booking:line.couldNotStart')} body={phase.error} />;
  }

  return (
    <LineSessionContext.Provider value={phase.session}>
      <Outlet />
    </LineSessionContext.Provider>
  );
}

export function Notice({ title, body }: { title: string; body?: string }) {
  return (
    <main className="mx-auto max-w-lg px-4 py-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      {body ? <p className="mt-2 text-xs text-bb-muted">{body}</p> : null}
    </main>
  );
}
